import { useEffect, useMemo, useState } from 'react';
import { FanucWidget, Readout } from './FanucWidget';
import { extractTlmTools, toolWearLevel, extractDroAxes, extractSpindle, formatDroMm, extractOffsets, formatOffsetMm } from './hmiTools';
import { sortHmiFields } from './hmiFieldOrdering';
import './hmiThemes.css';
import './fanucScreen.css';
import PmcPage from './PmcPage';
import MdiKeypad from './MdiKeypad';

/**
 * The six FANUC bottom function keys and which field sections each page shows.
 * Sections are the declarative grouping key (field.hmiResolved.section) — the
 * page a field lands on is derived from metadata, never from its fieldKey.
 */
// FANUC soft keys come in banks: the operator bank is always visible; the
// engineer/FOCAS bank is revealed by the continuous-menu ">" key (like a real
// control's menu-continuation soft key). The system bank is live-only — it
// never appears in the Studio designer/preview.
const PRIMARY_KEYS = ['POS', 'PROG', 'OFFSET', 'TOOL', 'ALARM', 'SETTING'];
// PMC sits in the engineer bank, which is where it lives on a real control (SYSTEM -> PMC). It is
// self-contained — it renders from the machine's own SIG_* state and needs no systemScreens payload.
const SYSTEM_KEYS = ['SYSTEM', 'PMC', 'DGNOS', 'ADAPTER'];
// Placeholder copy until each system screen's content lands (scaffold step).
const SYSTEM_PLACEHOLDER = {
  SYSTEM: 'FOCAS addressing & capabilities',
  DGNOS: 'sim ↔ wire drift diagnostics',
  ADAPTER: 'adapter engine slice & resolved addresses',
};
const PAGE_SECTIONS = {
  POS: ['execution', 'cycle', 'feed'],
  PROG: ['program'],
  OFFSET: [],
  TOOL: ['tlm'],
  ALARM: ['alarms'],
  SETTING: ['feed', 'cycle', 'program', 'misc'],
};
const SECTION_HOME_PAGE = {
  execution: 'POS',
  cycle: 'POS',
  feed: 'POS',
  program: 'PROG',
  tlm: 'TOOL',
  alarms: 'ALARM',
  misc: 'SETTING',
};
const SECTION_TITLE = {
  execution: 'STATUS',
  cycle: 'CYCLE',
  feed: 'FEED / OVERRIDE',
  program: 'PROGRAM',
  tlm: 'TOOL LIFE',
  alarms: 'ALARM',
  misc: 'SETTING',
};

const isTlmGrid = (f) => f.hmiResolved?.widget === 'tlm_grid';

function ToolLifeGrid({ tools, interactive, onReset, onOpenTable }) {
  return (
    <div className="fanuc-cell p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="fanuc-label text-[10px] uppercase">Tool life</span>
        {interactive && (
          <div className="flex gap-1">
            {onOpenTable && (
              <button
                type="button"
                onClick={onOpenTable}
                className="text-[10px] px-2 py-0.5 rounded border border-current/50 hover:bg-white/10"
              >
                TABLE
              </button>
            )}
            <button
              type="button"
              onClick={onReset}
              className="text-[10px] px-2 py-0.5 rounded border border-current/50 hover:bg-white/10"
            >
              RESET ALL
            </button>
          </div>
        )}
      </div>
      {tools.length === 0 ? (
        <div className="fanuc-label text-xs py-2">No tool-life counters defined for this type.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {tools.map((t) => {
            const level = toolWearLevel(t.pct);
            return (
              <div key={t.key} className="flex items-center gap-2 text-sm">
                <span className="fanuc-value font-bold w-10 shrink-0">{t.key}</span>
                <div className="fanuc-tlbar h-2.5 flex-1 rounded overflow-hidden">
                  <span className={`tl-${level}`} style={{ width: `${Math.min(100, t.pct)}%` }} />
                </div>
                <span className="fanuc-label text-xs tabular-nums w-24 text-right shrink-0">
                  {t.count}/{t.preset} · {t.pct}%
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * State-derived motion block for the POS page: the axis DRO + spindle readout.
 * Reads the POS_ and SPINDLE_ keys straight off live state (like the TLM grid) — not from
 * field metadata — so it renders whatever axes the engine happens to push and
 * needs no schema row. Hidden entirely when the state carries no motion keys.
 */
function MotionBlock({ dro, spindle }) {
  if (!dro.length && !spindle) return null;
  return (
    <section className="mb-3">
      <h3 className="fanuc-label text-[10px] uppercase tracking-widest mb-1.5">POSITION</h3>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
        {dro.length > 0 && (
          <div className="fanuc-cell px-3 py-2 lg:col-span-2">
            <span className="fanuc-label text-[10px] uppercase">Absolute</span>
            <div className="mt-1 flex flex-col gap-0.5">
              {dro.map((a) => (
                <div key={a.axis} className="flex items-baseline gap-3">
                  <span className="fanuc-value font-bold text-xl w-6 shrink-0">{a.axis}</span>
                  <span className="fanuc-value font-bold text-2xl tabular-nums flex-1 text-right">
                    {formatDroMm(a.value)}
                  </span>
                  <span className="fanuc-label text-xs w-8 shrink-0">mm</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {spindle && (
          <div className="fanuc-cell px-3 py-2 flex flex-col justify-center gap-2">
            {spindle.rpm != null && (
              <div className="flex items-baseline justify-between gap-2">
                <span className="fanuc-label text-[10px] uppercase">Spindle</span>
                <span className="fanuc-value font-bold text-2xl tabular-nums">
                  {spindle.rpm}
                  <span className="fanuc-label text-xs ml-1">rpm</span>
                </span>
              </div>
            )}
            {spindle.load != null && (
              <div>
                <div className="flex items-baseline justify-between">
                  <span className="fanuc-label text-[10px] uppercase">Load</span>
                  <span className="fanuc-value text-sm tabular-nums">{spindle.load}%</span>
                </div>
                <div className="fanuc-tlbar h-2.5 rounded overflow-hidden mt-1">
                  <span
                    className={`tl-${toolWearLevel(spindle.load)}`}
                    style={{ width: `${Math.max(0, Math.min(100, spindle.load))}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Prominent cycle-progress bar for the POS page (state-derived: cycle_progress 0–100).
 * When the engine reports cycle_start_time + cycle_duration and the machine is
 * running, it also shows a live elapsed/total + next-part countdown.
 */
function CycleProgressBar({ progress, startTime, duration, active }) {
  const pct = Math.max(0, Math.min(100, Number(progress) || 0));
  const start = Number(startTime);
  const dur = Number(duration);
  const hasEta = active && Number.isFinite(start) && start > 0 && Number.isFinite(dur) && dur > 0;

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!hasEta) return undefined;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [hasEta]);

  const total = dur / 1000;
  const elapsed = hasEta ? Math.min(total, Math.max(0, (now - start) / 1000)) : 0;
  const remaining = Math.max(0, total - elapsed);

  return (
    <div className="fanuc-cell px-3 py-2 mb-3">
      <div className="flex items-baseline justify-between mb-1">
        <span className="fanuc-label text-[10px] uppercase tracking-widest">Cycle</span>
        <span className="fanuc-value font-bold text-lg tabular-nums">{Math.round(pct)}%</span>
      </div>
      <div className="fanuc-tlbar h-3 rounded overflow-hidden">
        <span className="tl-ok" style={{ width: `${pct}%` }} />
      </div>
      {hasEta && (
        <div className="flex items-baseline justify-between mt-1 fanuc-label text-[11px] tabular-nums">
          <span>{elapsed.toFixed(1)}s / {total.toFixed(1)}s</span>
          <span>next part in {remaining.toFixed(1)}s</span>
        </div>
      )}
    </div>
  );
}

const TRACE_LEN = 6;
const hasBlock = (b) => b != null && b !== '';
const blockLoc = (b) => (b.sequence != null ? `N${b.sequence}` : b.line != null ? `L${b.line}` : '');

/**
 * The PROG-page "program check" — a rolling scrollback of the last few executing
 * G-code blocks (state.block/line/sequence), most recent highlighted, so PROG
 * shows the program walking rather than static metadata. Seeded from props so it
 * paints immediately; appends distinct blocks as they arrive. Hidden when no
 * VCNC is driving (foundry / idle / no executable program).
 */
function ExecBlockPanel({ block, line, sequence }) {
  const [trace, setTrace] = useState(() => (hasBlock(block) ? [{ block, line, sequence }] : []));
  useEffect(() => {
    if (!hasBlock(block)) return;
    setTrace((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.block === block && last.line === line) return prev; // same block, still cutting
      return [...prev, { block, line, sequence }].slice(-TRACE_LEN);
    });
  }, [block, line, sequence]);

  if (!trace.length) return null;
  const curIdx = trace.length - 1;
  const liveCurrent = hasBlock(block); // last entry is the live block only while one is executing
  return (
    <section className="mb-3">
      <h3 className="fanuc-label text-[10px] uppercase tracking-widest mb-1.5">Program check</h3>
      <div className="fanuc-cell px-3 py-2.5 font-mono">
        {trace.map((b, i) => {
          const cur = i === curIdx && liveCurrent;
          return (
            <div key={`${i}-${b.line}`} className={`flex items-baseline gap-3 py-0.5 ${cur ? '' : 'opacity-40'}`}>
              <span className="fanuc-label text-xs tabular-nums w-10 shrink-0 text-right">{blockLoc(b)}</span>
              <span className={`flex-1 truncate fanuc-value ${cur ? 'font-bold text-base' : 'text-sm'}`}>
                {cur ? '▸ ' : '  '}{b.block}
              </span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** Wraps a field cell so the Studio can click-to-select it. */
function Cell({ field, designMode, selectedFieldId, onSelectField, children }) {
  if (!designMode) return children;
  const selected = selectedFieldId != null && field?.id === selectedFieldId;
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Select field ${field?.fieldKey}`}
      className={`fanuc-selectable rounded ${selected ? 'fanuc-selected' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onSelectField?.(field?.id);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelectField?.(field?.id);
        }
      }}
    >
      {/* controls are inert in design mode — the cell captures the click to select */}
      <div className="pointer-events-none">{children}</div>
    </div>
  );
}

/**
 * Declarative FANUC operator screen. The SAME component renders the live
 * per-machine HMI (operator) and the Studio preview (designer) — the only
 * difference is `designMode` + mock vs socket `values`.
 */
/**
 * OFFSET / WEAR — the tool wear table, laid out like the real control's page: rows `W001…`, columns
 * `X | Z | R`, three decimal places, millimetres.
 *
 * Values come from the engine, which owns them (live = setup baseline + approved correction + wear).
 * A BLANK cell is meaningful: it means this machine class does not carry that family at all, which is
 * why a mill shows nothing under Z rather than 0.000. Read-only for now — writing goes through an
 * approval, not this screen (focas-playground/docs/31 §8).
 */
function OffsetWearTable({ rows, program, partComment }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="fanuc-cell p-6 text-center fanuc-label text-sm">
        OFFSET / WEAR — no tool offsets published. A machine publishes these once it is running an
        operation with tooling; foundry lines never do.
      </div>
    );
  }
  const hasZ = rows.some((r) => r.Z != null);
  // The cursor sits on the first non-zero Z, which is where an operator's attention actually is —
  // on the machine it was parked on W002's Z, the value they had just edited.
  const cursorKey = (rows.find((r) => r.Z) || rows[0]).key;

  return (
    <div className="px-1 inline-block min-w-[420px]">
      {/* Header band, as the real screen has it: title left, part comment centre, O-number right. */}
      <div className="flex items-baseline justify-between gap-6 mb-1 text-[12px]">
        <span className="fanuc-label tracking-wide">OFFSET / WEAR</span>
        <span className="fanuc-value">{partComment || ''}</span>
        <span className="fanuc-value font-bold">{program || 'O----'}</span>
      </div>

      <table className="fanuc-otable font-mono tabular-nums">
        <thead>
          <tr>
            <th className="text-left">NO.</th>
            <th>X</th>
            <th>Z</th>
            <th>R</th>
            <th>T</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              <td className="is-rowlabel">
                W{String(r.slot).padStart(3, '0')}
                {/^[Tt]?\d+$/.test(r.key) ? '' : ` ${r.key}`}
              </td>
              <td>{formatOffsetMm(r.X)}</td>
              <td className={r.key === cursorKey ? 'is-cursor' : ''}>{formatOffsetMm(r.Z)}</td>
              <td>{formatOffsetMm(r.R)}</td>
              {/* T is the geometry/tool-nose column on the real screen. We do not model nose radius
                  orientation, so it reads 0 rather than being omitted — the column exists on the
                  machine and an operator looks for it. */}
              <td>0</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!hasZ && (
        <div className="fanuc-label text-[10px] mt-2">
          Z is blank: a machining centre carries length and radius compensation, not per-tool Z wear.
        </div>
      )}
    </div>
  );
}


export default function FanucScreen({
  machineType,
  fields = [],
  values = {},
  bridge,
  availablePrograms = {},
  previewMode = false,
  designMode = false,
  selectedFieldId = null,
  onSelectField,
  onOpenToolManager,
  onOpenProgramManager,
  systemScreens = null,
  onReset,
  initialPage = null,
  onSetMode,
}) {
  const theme = machineType?.hmiTheme || 'fanuc';
  const typeLabel = machineType?.family || machineType?.code || 'CNC';
  const interactive = !designMode; // in the designer, cells select instead of operate

  const sorted = useMemo(
    () =>
      sortHmiFields(
        (fields || []).map((f) => ({ ...f, hmiResolved: f.hmiResolved || {} }))
      ),
    [fields]
  );

  const bySection = useMemo(() => {
    const m = new Map();
    for (const f of sorted) {
      const sec = f.hmiResolved?.section || 'misc';
      if (!m.has(sec)) m.set(sec, []);
      m.get(sec).push(f);
    }
    return m;
  }, [sorted]);

  // START/STOP is driven by the execution field's enum states — declaratively,
  // not by fieldKey. Works for any enum status field in the execution section.
  const execField = useMemo(
    () => sorted.find((f) => f.hmiResolved?.section === 'execution' && f.dataType === 'enum'),
    [sorted]
  );
  const execStates = useMemo(() => {
    if (!execField) return [];
    try {
      const p =
        typeof execField.enumOptions === 'string'
          ? JSON.parse(execField.enumOptions)
          : execField.enumOptions;
      return Array.isArray(p) ? p.map(String) : [];
    } catch {
      return [];
    }
  }, [execField]);
  const runState = execStates.find((s) => /ACTIVE|RUN|ON|START/i.test(s)) || execStates[execStates.length - 1];
  const idleState = execStates.find((s) => s !== runState) || execStates[0];
  const execVal = execField ? values[execField.fieldKey] : undefined;
  const isRunning = execVal === runState;

  const faulted = !!values.faulted;
  const alarmCode = values.alarm_code;
  const alarmMsg = values.alarm_message;

  const programOptions = useMemo(() => Object.keys(availablePrograms || {}), [availablePrograms]);

  const tools = useMemo(() => extractTlmTools(values), [values]);
  // OFFSET / WEAR rows. A family the machine does not carry comes back null and renders blank —
  // a mill has no per-tool Z wear, and printing 0.000 there would claim a value the control lacks.
  const offsetRows = useMemo(() => extractOffsets(values), [values]);

  const dro = useMemo(() => extractDroAxes(values), [values]);
  const spindle = useMemo(() => extractSpindle(values), [values]);
  const hasMotion = dro.length > 0 || !!spindle;

  // `?hmiPage=PMC` deep-links a screen. A control's screens are places you navigate to; making
  // them addressable costs nothing and is what lets this panel be checked without clicking.
  const [page, setPage] = useState(initialPage || 'POS');
  const [menuBank, setMenuBank] = useState(
    initialPage && SYSTEM_KEYS.includes(initialPage) ? 'system' : 'primary'); // 'primary' | 'system'
  // The engineer/FOCAS bank is live-only and only appears when the host passes
  // system screens (FOCAS data). Only keys with a provided node are shown.
  const availSystemKeys = SYSTEM_KEYS.filter((k) => k === 'PMC' || systemScreens?.[k]);
  const showSystemBank = !designMode && !previewMode && availSystemKeys.length > 0;
  // When a field is selected in the Studio, jump to the page that shows it.
  useEffect(() => {
    if (!designMode || selectedFieldId == null) return;
    const f = sorted.find((x) => x.id === selectedFieldId);
    const home = f && SECTION_HOME_PAGE[f.hmiResolved?.section];
    if (home) setPage(home);
  }, [selectedFieldId, designMode, sorted]);

  const toggleExec = () => {
    if (!execField || execStates.length < 2) return;
    bridge.apply(execField.fieldKey, isRunning ? idleState : runState);
  };

  // Hardware-pendant navigation: F1–F6 select the visible soft keys, Backquote
  // flips the bank. Ignored in the Studio and while typing in a field so it
  // never fights a numeric input on the SYSTEM page.
  const visibleKeys = menuBank === 'primary' ? PRIMARY_KEYS : availSystemKeys;
  const visibleKeysSig = visibleKeys.join('|');
  useEffect(() => {
    if (designMode) return undefined;
    const keys = visibleKeysSig ? visibleKeysSig.split('|') : [];
    const onKey = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target;
      const tag = el?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el?.isContentEditable) return;
      const fn = /^F([1-6])$/.exec(e.key);
      if (fn) {
        const idx = Number(fn[1]) - 1;
        if (idx < keys.length) { e.preventDefault(); setPage(keys[idx]); }
      } else if (e.key === '`' && showSystemBank) {
        e.preventDefault();
        setMenuBank((b) => (b === 'primary' ? 'system' : 'primary'));
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [designMode, visibleKeysSig, showSystemBank]);

  const renderSection = (sec) => {
    const list = bySection.get(sec) || [];
    if (!list.length) return null;
    const hasGrid = list.some(isTlmGrid);
    const cells = list.filter((f) => !isTlmGrid(f));
    if (!hasGrid && cells.length === 0) return null;
    const gridAnchor = list.find(isTlmGrid);
    return (
      <section key={sec} className="mb-3">
        <h3 className="fanuc-label text-[10px] uppercase tracking-widest mb-1.5">
          {SECTION_TITLE[sec] || sec}
        </h3>
        {hasGrid && (
          <Cell
            field={gridAnchor}
            designMode={designMode}
            selectedFieldId={selectedFieldId}
            onSelectField={onSelectField}
          >
            <ToolLifeGrid
              tools={tools}
              interactive={interactive && !previewMode}
              onReset={() => bridge.resetAllToolCounts(tools.map((t) => t.key))}
              onOpenTable={onOpenToolManager}
            />
          </Cell>
        )}
        {cells.length > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
            {cells.map((f) => (
              <Cell
                key={f.id}
                field={f}
                designMode={designMode}
                selectedFieldId={selectedFieldId}
                onSelectField={onSelectField}
              >
                <FanucWidget
                  field={f}
                  values={values}
                  bridge={bridge}
                  programOptions={programOptions}
                  interactive={interactive}
                />
              </Cell>
            ))}
          </div>
        )}
      </section>
    );
  };

  const pageSections = PAGE_SECTIONS[page] || [];
  const anyOnPage = pageSections.some((s) => (bySection.get(s) || []).length);
  // POS leads with the live DRO/spindle even when no POS field sections exist.
  const showEmpty = !anyOnPage && !(page === 'POS' && hasMotion);

  return (
    <div data-hmi-theme={theme} className="fanuc-screen w-full max-w-4xl flex flex-col select-none">
      {/* Title / mode strip */}
      <div className="fanuc-titlebar flex items-center justify-between px-4 py-2 rounded-t">
        <div className="flex items-baseline gap-3 min-w-0">
          <span className="font-extrabold tracking-wide truncate">{machineType?.name || typeLabel}</span>
          <span className="fx-sub text-xs font-mono uppercase truncate">{typeLabel}</span>
        </div>
        <div className="flex items-center gap-3 text-xs font-mono font-semibold">
          <span className="fx-sub">MODE {values.cycleMode ?? '—'}</span>
          <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded"
            style={{ background: 'rgba(0,0,0,0.15)' }}
          >
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ background: faulted ? '#ef4444' : isRunning ? '#22c55e' : '#eab308' }}
            />
            {faulted ? 'ALARM' : isRunning ? 'RUN' : 'READY'}
          </span>
        </div>
      </div>

      {/* Program headline (real data bindings; a real POS screen leads with the O-number) */}
      <div className="px-4 pt-3 pb-1 flex items-end justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <span className="fanuc-label text-[10px] uppercase">Program</span>
          <div className="fanuc-value font-bold text-3xl tabular-nums truncate">
            {values.program ? String(values.program) : 'O----'}
          </div>
          {values.program_header ? (
            <div className="fanuc-label text-xs font-mono truncate">{String(values.program_header)}</div>
          ) : null}
        </div>
        <div className="flex gap-2">
          <Readout label="Parts" value={values.part_count ?? 0} big />
          {values.target_qty != null && <Readout label="Target" value={values.target_qty} big />}
        </div>
      </div>

      {/* Main page area — a FIXED window, see .fanuc-crt. A control's screen does not grow because
          the page has more rows on it; the page scrolls inside the glass. */}
      <div className="fanuc-crt px-4 py-3">
        {page === 'OFFSET' ? (
          <OffsetWearTable rows={offsetRows} program={values.program} partComment={values.program_header} />
        ) : page === 'PMC' ? (
          <PmcPage values={values} />
        ) : SYSTEM_KEYS.includes(page) ? (
          systemScreens?.[page] || (
            <div className="fanuc-cell p-6 text-center">
              <div className="fanuc-value font-bold text-sm tracking-widest">{page}</div>
              <div className="fanuc-label text-xs mt-1">{SYSTEM_PLACEHOLDER[page]}</div>
            </div>
          )
        ) : (
          <>
            {page === 'POS' && (
              <CycleProgressBar
                progress={values.cycle_progress}
                startTime={values.cycle_start_time}
                duration={values.cycle_duration}
                active={isRunning}
              />
            )}
            {page === 'POS' && <MotionBlock dro={dro} spindle={spindle} />}
            {page === 'PROG' && (
              <ExecBlockPanel block={values.block} line={values.line} sequence={values.sequence} />
            )}
            {page === 'PROG' && onOpenProgramManager && (
              <div className="mb-3 flex justify-end">
                <button
                  type="button"
                  onClick={onOpenProgramManager}
                  className="text-xs font-bold tracking-wider px-3 py-1.5 rounded border border-current/50 fanuc-value hover:bg-white/10"
                >
                  PROGRAM MANAGER
                </button>
              </div>
            )}
            {anyOnPage && pageSections.map(renderSection)}
            {showEmpty && (
              <div className="fanuc-cell p-6 text-center fanuc-label text-sm">
                No fields on this page for {machineType?.code || 'this type'}.
              </div>
            )}
          </>
        )}
      </div>

      {/* Alarm line */}
      <div className="px-4 pb-2">
        <div
          className={`px-3 py-1.5 rounded border text-sm font-mono font-semibold ${
            faulted ? 'fanuc-alarm-bad' : 'fanuc-alarm-ok'
          }`}
        >
          {faulted
            ? `⚠ ALM ${alarmCode ?? ''} ${alarmMsg ?? 'MACHINE FAULT'}`.trim()
            : '● NO ALARM'}
        </div>
      </div>


      {/* Soft-key bar. A real FANUC has a continuation arrow at BOTH ends — ◀ steps back through the
          menu chain and ▶ forward — with the function keys between them. Ours has two banks
          (operator and engineer) so both arrows drive the same toggle, which is what a two-bank
          menu does. */}
      <div className="fanuc-softkey flex gap-1 p-1.5 rounded-b items-stretch">
        <button
          type="button"
          onClick={() => setMenuBank((b) => (b === 'primary' ? 'system' : 'primary'))}
          disabled={!showSystemBank}
          className="px-2 rounded shrink-0 disabled:opacity-30"
          title="Previous menu"
          aria-label="Previous menu"
        >
          ◀
        </button>

        <div className="grid gap-1 flex-1 grid-cols-6">
          {(menuBank === 'primary' ? PRIMARY_KEYS : availSystemKeys).map((k, i) => (
            <button
              key={k}
              type="button"
              onClick={() => setPage(k)}
              title={`${k} (F${i + 1})`}
              className={`py-1.5 rounded ${page === k ? 'is-active' : ''}`}
            >
              <span className="block text-[8px] leading-none opacity-45 tracking-normal">F{i + 1}</span>
              <span className="block text-xs font-bold tracking-wider">{k}</span>
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setMenuBank((b) => (b === 'primary' ? 'system' : 'primary'))}
          disabled={!showSystemBank}
          className="px-2 rounded shrink-0 disabled:opacity-30"
          title="Next menu"
          aria-label="Next menu"
        >
          ▶
        </button>
      </div>

      {!designMode && !previewMode && (
        <MdiKeypad
          availablePages={[...PRIMARY_KEYS, ...availSystemKeys]}
          onPage={(p) => {
            setPage(p);
            setMenuBank(PRIMARY_KEYS.includes(p) ? 'primary' : 'system');
          }}
          onReset={onReset}
        />
      )}

      {/* Operator panel. On the real machine these are NOT on the screen — they are on a separate grey
          panel below the keyboard, which is where they are now. Having them inside the CRT was the
          single thing that made this read as a dashboard with a green border rather than a control. */}
      {(execField || !designMode) && (
        <div className="fanuc-oppanel">
          {/* Mode switches. On the machine these are latching buttons on the operator panel with a
              lamp in the cap, and an operator uses them constantly — BLOCK DELETE to skip the /
              blocks, SINGLE BLOCK to step a program one line at a time. Both are backed by real
              endpoints, so the lamp reflects the control rather than a local toggle. */}
          {!designMode && onSetMode && (
            <div className="fanuc-modes">
              <button
                type="button"
                className={`fanuc-modekey ${values.blockDelete ? 'is-on' : ''}`}
                onClick={() => onSetMode('blockDelete', !values.blockDelete)}
                title="Skip blocks prefixed with /"
              >
                <span className="fanuc-lamp" />BLOCK<br />DELETE
              </button>
              <button
                type="button"
                className={`fanuc-modekey ${String(values.cycleMode).toUpperCase() === 'SINGLE' ? 'is-on' : ''}`}
                onClick={() => onSetMode('cycleMode',
                  String(values.cycleMode).toUpperCase() === 'SINGLE' ? 'AUTO' : 'SINGLE')}
                title="Execute one block per cycle start"
              >
                <span className="fanuc-lamp" />SINGLE<br />BLOCK
              </button>
            </div>
          )}

          {/* CYCLE START — kicks engine-managed production (successor to the
              deprecated /run-cycle). Hidden in the Studio designer/preview. */}
          {!designMode && (
            <button
              type="button"
              onClick={() => bridge.startCycle()}
              className="px-4 h-16 rounded-lg border-2 border-black/40 bg-green-700 hover:bg-green-800 text-white font-extrabold text-sm tracking-wider leading-tight shadow-lg"
            >
              CYCLE
              <br />
              START
            </button>
          )}
          {execField && (
            <button
              type="button"
              disabled={designMode || execStates.length < 2}
              onClick={toggleExec}
              className="w-24 h-24 rounded-full border-4 border-black/40 font-extrabold text-white shadow-lg disabled:opacity-70"
              style={{ background: isRunning ? '#b91c1c' : '#15803d' }}
            >
              {isRunning ? 'STOP' : 'START'}
            </button>
          )}
          {execField && (
            <button
              type="button"
              disabled={designMode}
              onClick={() => bridge.emergencyStop()}
              className="w-20 h-20 rounded-full border-4 border-yellow-400 bg-red-600 hover:bg-red-700 text-yellow-200 font-extrabold text-xs shadow-lg disabled:opacity-70"
              aria-label="Emergency stop"
            >
              E-STOP
            </button>
          )}
        </div>
      )}
    </div>
  );
}
